import { Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { DealOption, DealOptionProduct } from '../../../../../models/deal-options-view.models';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmHelperService } from '@zayan-erp/shared';
import { DealOptionsService } from '@zayan-erp/sales';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CurrencyFormatPipe, SvgIconsComponent } from '@zayan-erp/shared';
import { SalesApsService } from '@zayan-erp/sales';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService } from '@zayan-erp/shared';

@Component({
  selector: 'deal-addendum-product-preview',
  imports: [
    AccordionModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    CurrencyFormatPipe,
    NgbDropdownModule,
    SvgIconsComponent
  ],
  providers: [],
  templateUrl: './deal-addendum-product-preview.component.html'
})
export class DealAddendumProductPreviewComponent {
  private confirmationService = inject(ConfirmHelperService);
  private dealOptionsService = inject(DealOptionsService);
  private destroyRef = inject(DestroyRef);
  private salesApsService = inject(SalesApsService);
  private abilityService = inject(AbilityService);

  addendum = input.required<DealOption>();
  product = input.required<DealOptionProduct>();
  isKitLinkedProduct = input<boolean>(false);

  onChanges = output<void>();
  readonly isLocked = computed(() => this.addendum().isLocked);
  /** Single source of truth for this component's write gate. */
  readonly canWriteDeals = computed(() => this.abilityService.ability.can('write-sales-deals', 'deals'));
  readonly canDelete = computed(() => !this.isLocked() && this.canWriteDeals());
  readonly canCancel = computed(
    () =>
      this.isLocked() &&
      !this.product().isCancelled &&
      this.abilityService.ability.can('cancel-locked-addendum', 'deals') &&
      this.canWriteDeals()
  );

  readonly defaultProductImageUrl = 'assets/images/img-placeholder.png';

  showConfirmation(event: Event, action: 'cancel' | 'delete') {
    const isDelete = action === 'delete';
    this.confirmationService
      .showModal({
        icon: 'confirm-icon',
        message: isDelete
          ? 'Are you sure you want to delete this product? This action is permanent and cannot be undone.'
          : 'Do you want to cancel this product? This action is permanent and cannot be undone.',
        header: isDelete ? 'Delete Product?' : 'Cancel Product?',
        acceptLabel: isDelete ? 'Delete' : 'Cancel Product',
        acceptSeverity: isDelete ? 'danger' : 'primary',
        rejectLabel: 'No'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          isDelete ? this.deleteProduct() : this.cancelProduct();
        }
      });
  }

  deleteProduct() {
    if (!this.canWriteDeals()) return;
    this.dealOptionsService
      .deleteAddendumProduct(this.product().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.onChanges.emit();
        },
        error: error => {
          console.error('error deleting product', error);
        }
      });
  }

  cancelProduct(): void {
    if (!this.canWriteDeals()) return;
    this.dealOptionsService
      .cancelAddendumProduct(this.product().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.salesApsService.realoadDeal.next(null);
          this.onChanges.emit();
        }
      });
  }
}
